import {
  Band,
  BRAND_RAMP,
  DotItem,
  Panel,
  panelClasses,
  Pill,
  SectionTitle,
  BACK_LINK,
} from '@/components/bedrock';
import { PageHero } from '@/components/marketing-ui';
import { useT } from '@/core/I18nProvider';
import {
  GRANDFATHERING_RULES,
  PRICING_COMMITMENTS,
  pricingChangeEntriesNewestFirst,
  type PricingChangeEntry,
} from '@/features/pricing-history';
import { localizedPath } from '@/i18n/locale-path';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import type { Translator } from '@/i18n/translator';
import type { ReactElement } from 'react';

/**
 * Public pricing-history page (AF-234) at `/pricing/history`.
 *
 * The audit trail behind `/pricing`: every price change documented with date +
 * rationale, existing customers grandfathered, MFN refused. Renders the
 * `docs/pricing/pricing-history.md` SOT — the two commitments, the
 * grandfathering policy, the MFN-refusal note, and the append-only change-log
 * (newest-first). Removes the "price-hike risk" procurement objection.
 *
 * ## Composition
 *
 * The masthead was ported to `PageHero` in an earlier pass; the BODY was not,
 * and stayed four `<h2 class="text-xl">` sections of `text-sm` grey separated
 * by `mt-10` — no ground change, no card, no accent, nothing for the eye to
 * land on down a long page. On a page whose whole job is reassurance, the most
 * reassuring content on the site read as the least considered.
 *
 * It is now a stack of `Band`s like every other route, and each section is
 * given the primitive its content actually is:
 *
 * - the two COMMITMENTS are promises, so they take the page's one `accent`
 *   Panel and render as `DotItem`s in the brand ramp — the strongest object on
 *   the page, because they are the reason a buyer opened it;
 * - the five GRANDFATHERING rules are a lookup ("what happens if…"), so they
 *   are cards on the quiet `sunk` ground with the rule KIND as a `Pill`,
 *   scannable rather than read top-to-bottom;
 * - the MFN refusal is one short paragraph and stays prose — a card around two
 *   sentences would be decoration, and the point of the rhythm is that not
 *   everything is a card;
 * - each CHANGELOG entry becomes its own card, with a hairline between its
 *   header and its seven fields, so a record reads as one object instead of as
 *   more grey. The entry TYPE is untouched — restyling the date label would be
 *   a different change riding along inside this one.
 *
 * The `data-rule` / `data-pricing-entry` hooks are unchanged — they are the
 * SOT-drift guards' handles, and a recomposition must not move them.
 */
export function PricingHistory(): ReactElement {
  const { t, locale } = useT();
  useLocalizedSeoMeta({
    title: t('pricing.history.page.title'),
    description: t('pricing.history.page.description'),
    path: '/pricing/history',
  });
  const entries = pricingChangeEntriesNewestFirst();

  return (
    <main id="main" tabIndex={-1} className="min-h-screen bg-[color:var(--bd-page)]">
      {/*
        The shared masthead, not a fourth hand-rolled one.

        This page and three trust documents kept their pre-redesign
        `<h1 class="text-3xl">` on a blank white column while every other route
        moved to `PageHero` / `LegalPage`. Measured over the built site they
        were the only four routes painting no brand object anywhere, and their
        type scale differed from the rest of the site by two steps — precisely
        the drift `LegalPage`'s own doc comment says it was extracted to stop.

        The "current pricing" link becomes the eyebrow, matching the breadcrumb
        every sibling document carries. It reads as a way BACK because that is
        what it is: this page is the audit trail behind `/pricing`, reached
        from it.
      */}
      <PageHero
        align="start"
        width="prose"
        eyebrow={
          <a className={BACK_LINK} href={localizedPath('/pricing', locale)}>
            {t('pricing.history.currentPricing')}
          </a>
        }
        title={t('pricing.history.page.heading')}
        description={t('pricing.history.page.intro')}
      />

      <Band as="section" space="sm" aria-labelledby="commitments-heading">
        <div className="mx-auto max-w-3xl">
          <SectionTitle id="commitments-heading">
            {t('pricing.history.commitments.heading')}
          </SectionTitle>
          <Panel accent className="mt-6 p-6 sm:p-7">
            <ul className="flex flex-col gap-5">
              {PRICING_COMMITMENTS.map((commitment, index) => (
                <DotItem
                  key={commitment.id}
                  color={BRAND_RAMP[index % BRAND_RAMP.length] ?? BRAND_RAMP[0]}
                  title={t(commitment.titleKey)}
                >
                  {t(commitment.detailKey)}
                </DotItem>
              ))}
            </ul>
          </Panel>
        </div>
      </Band>

      <Band as="section" tone="sunk" space="sm" aria-labelledby="grandfathering-heading">
        <div className="mx-auto max-w-3xl">
          <SectionTitle id="grandfathering-heading">
            {t('pricing.history.grandfathering.heading')}
          </SectionTitle>
          <p className="mt-3 text-[0.95rem] leading-relaxed text-[color:var(--bd-body)]">
            {t('pricing.history.grandfathering.intro')}
          </p>
          {/* `<div>` between `<dl>` and its `<dt>`/`<dd>` is the one wrapper
              HTML5 allows, so the card look comes from `panelClasses` rather
              than from `<Panel>` — nesting the component would put a second
              div in there and break the list semantics for a screen reader. */}
          {/* One column, with the kind PILL in a fixed left track rather than
              stacked above the rule.

              A two-column grid was the obvious first shape and it was wrong for
              this content: five is odd, so the last card sat alone on its own
              row with a card-width hole beside it — the reader's eye reads that
              gap as "something failed to load" rather than as the end of a
              list. A fixed label track fills the width without inventing a
              sixth rule, and gives the five kinds one x to be scanned down,
              which is how a "what happens if…" lookup is actually used. */}
          <dl className="mt-6 flex flex-col gap-3">
            {GRANDFATHERING_RULES.map((rule) => (
              <div
                key={rule.id}
                data-rule={rule.id}
                className={panelClasses({
                  className: 'grid items-start gap-x-5 gap-y-2 p-5 sm:grid-cols-[11rem_1fr]',
                })}
              >
                <dt>
                  <Pill>{t(rule.kindKey)}</Pill>
                </dt>
                <dd className="text-sm leading-relaxed text-[color:var(--bd-body)]">
                  {t(rule.ruleKey)}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </Band>

      <Band as="section" space="sm" aria-labelledby="mfn-heading">
        <div className="mx-auto max-w-3xl">
          <SectionTitle id="mfn-heading">{t('pricing.history.mfn.heading')}</SectionTitle>
          <p className="mt-3 text-[0.95rem] leading-relaxed text-[color:var(--bd-body)]">
            {t('pricing.history.mfn.body')}
          </p>
        </div>
      </Band>

      <Band as="section" tone="sunk" space="sm" aria-labelledby="changelog-heading">
        <div className="mx-auto max-w-3xl">
          <SectionTitle id="changelog-heading">
            {t('pricing.history.changelog.heading')}
          </SectionTitle>
          <ol
            aria-label={t('pricing.history.changelog.listLabel')}
            className="mt-6 flex flex-col gap-4"
          >
            {entries.map((entry) => (
              <ChangeEntry key={`${entry.date}-${entry.summaryKey}`} entry={entry} t={t} />
            ))}
          </ol>
          <p className="mt-8 text-xs leading-relaxed text-[color:var(--bd-muted)]">
            {t('pricing.history.changelog.footnote')}
          </p>
        </div>
      </Band>
    </main>
  );
}

function ChangeEntry({
  entry,
  t,
}: {
  readonly entry: PricingChangeEntry;
  readonly t: Translator;
}): ReactElement {
  return (
    // A plain `<li>` wearing the panel classes rather than `<Panel as="li">`:
    // `PanelProps` forwards no `data-*`, and `data-pricing-entry` is the
    // SOT-drift guard's handle — losing it would make that test pass by
    // finding nothing.
    <li data-pricing-entry={entry.date} className={panelClasses({ className: 'p-6' })}>
      {/* Stacked, not baseline-inline. The date label runs from a short
          "CHANGED 2026-08-04 — POSTED PRICES IN FORCE" to a much longer
          "LAUNCHED … ENTRY FINALIZES WITH THE FIRST PAID SUBSCRIPTION", so an
          inline title started at a different x on every entry — and in a longer
          locale it wrapped away from the label it belongs to. Eyebrow over
          title is the relationship every other band on the site uses. */}
      <header className="flex flex-col gap-1.5">
        <span className="text-xs uppercase tracking-wide text-[color:var(--bd-muted)] rtl:normal-case rtl:tracking-normal">
          {t(entry.dateLabelKey)}
        </span>
        <h3 className="text-lg font-semibold text-[color:var(--bd-ink)]">{t(entry.summaryKey)}</h3>
      </header>
      {/* ONE grid for the whole record, with `<dt>`/`<dd>` as its direct
          children, so all seven values share a single x.

          They did not before: each field was its own flex row, so the value
          started wherever that row's label happened to end — seven different
          left edges down a dense seven-field record, which is most of why it
          read as a wall. A per-row layout cannot align across rows; only a
          shared grid can. The label track is `max-content` capped at a third of
          the card so a long label in German or French widens it without
          crushing the value column, and the whole thing collapses to a stack
          below `sm`. */}
      <dl className="mt-4 grid gap-x-5 gap-y-2 border-t border-[color:var(--bd-line)] pt-4 text-sm sm:grid-cols-[minmax(0,max-content)_1fr]">
        <Field label={t('pricing.history.field.change')} value={t(entry.changeKey)} />
        <Field label={t('pricing.history.field.priorPricing')} value={t(entry.priorPricingKey)} />
        <FieldLabel>{t('pricing.history.field.newPricing')}</FieldLabel>
        <dd className="text-[color:var(--bd-muted)]">
          <ul className="flex list-disc flex-col gap-0.5 ps-4">
            {entry.newPricingKeys.map((lineKey) => (
              <li key={lineKey}>{t(lineKey)}</li>
            ))}
          </ul>
        </dd>
        <Field label={t('pricing.history.field.affected')} value={t(entry.affectedKey)} />
        <Field label={t('pricing.history.field.grandfathered')} value={t(entry.grandfatheredKey)} />
        <Field label={t('pricing.history.field.why')} value={t(entry.whyKey)} />
      </dl>
    </li>
  );
}

/**
 * A `<>` fragment rather than a wrapping `<div>`: the parent `<dl>` is the
 * grid, so a wrapper would make each field its own one-row grid again and put
 * the misalignment straight back.
 */
function Field({ label, value }: { readonly label: string; readonly value: string }): ReactElement {
  return (
    <>
      <FieldLabel>{label}</FieldLabel>
      <dd className="text-[color:var(--bd-muted)]">{value}</dd>
    </>
  );
}

function FieldLabel({ children }: { readonly children: string }): ReactElement {
  return <dt className="font-medium text-[color:var(--bd-ink)]">{children}</dt>;
}
